import React, { useEffect, useState, useRef } from "react";
import io from "socket.io-client";
import axios from "axios";
import { Users, UserPlus, Search, Send, Settings, Trash2, Crown, User, MessageCircle, LogOut, X, CheckCircle, AlertCircle, Info, MoreVertical } from "lucide-react";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import "../style/chat.css";

const token = localStorage.getItem("token");
const socket = io("https://agro-nexus-prod.vercel.app/", {
    auth: { token },
});

export default function ChatsPage() {
    const [isOpen, setIsOpen] = useState(true);
    const [chats, setChats] = useState([]);
    const [selectedChat, setSelectedChat] = useState(null);
    const [messages, setMessages] = useState([]);
    const [message, setMessage] = useState("");
    const [search, setSearch] = useState("");
    const [foundUsers, setFoundUsers] = useState([]);
    const [typingUser, setTypingUser] = useState(null);
    const [currentUserId, setCurrentUserId] = useState(null);
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(false);

    const [notifications, setNotifications] = useState([]);

    const [showCreateGroup, setShowCreateGroup] = useState(false);
    const [groupName, setGroupName] = useState("");
    const [selectedUsers, setSelectedUsers] = useState([]);

    const [showGroupModal, setShowGroupModal] = useState(false);
    const [groupParticipants, setGroupParticipants] = useState([]);
    const [showAddMember, setShowAddMember] = useState(false);
    const [loadingParticipants, setLoadingParticipants] = useState(false);

    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);

    const [showChatOptions, setShowChatOptions] = useState(false);

    const [messageMenuOpen, setMessageMenuOpen] = useState(null);

    const messagesEndRef = useRef(null);

    axios.defaults.baseURL = "https://agro-nexus-prod.vercel.app/";
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    const showNotification = (message, type = 'info') => {
        const id = Date.now();
        setNotifications(prev => [...prev, { id, message, type }]);

        setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== id));
        }, 4000);
    };

    const showConfirm = (message, onConfirm) => {
        setConfirmAction({ message, onConfirm });
        setShowConfirmModal(true);
    };

    const handleConfirm = () => {
        if (confirmAction?.onConfirm) {
            confirmAction.onConfirm();
        }
        setShowConfirmModal(false);
        setConfirmAction(null);
    };

    const handleCancelConfirm = () => {
        setShowConfirmModal(false);
        setConfirmAction(null);
    };

    axios.defaults.baseURL = "https://agro-nexus-prod.vercel.app/";
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    useEffect(() => {
        fetchChats();
        fetchCurrentUser();
    }, []);

    const fetchCurrentUser = async () => {
        try {
            const res = await axios.get("/api/perfil");
            setCurrentUserId(res.data.usuario.id);
            setCurrentUser(res.data.usuario);
            console.log("👤 Usuario actual:", res.data.usuario);
        } catch (err) {
            console.error("❌ Error al obtener perfil", err);
        }
    };

    useEffect(() => {
        socket.on("receiveMessage", (msg) => {
            console.log("📩 Mensaje recibido:", msg);

            if (msg.chat_id === selectedChat?.id) {
                setMessages((prev) => {
                    if (prev.some(m => m.id === msg.id)) return prev;
                    return [...prev, msg];
                });
            }

            fetchChats();
        });

        socket.on("messageDeleted", (data) => {
            console.log("🗑️ Mensaje eliminado:", data);

            if (data.chat_id === selectedChat?.id) {
                setMessages((prev) => prev.filter(m => m.id !== data.message_id));
            }
        });

        socket.on("userTyping", ({ chatId, userId }) => {
            if (chatId === selectedChat?.id && userId !== currentUserId) {
                setTypingUser(userId);
                setTimeout(() => setTypingUser(null), 2000);
            }
        });

        return () => {
            socket.off("receiveMessage");
            socket.off("messageDeleted");
            socket.off("userTyping");
        };
    }, [selectedChat, currentUserId]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showChatOptions && !e.target.closest('.chat-options-menu')) {
                setShowChatOptions(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showChatOptions]);

    const fetchChats = async () => {
        try {
            const res = await axios.get("/api/chats");
            console.log("📋 Chats obtenidos:", res.data);
            setChats(res.data.chats || []);
        } catch (err) {
            console.error("❌ Error al cargar chats", err);
        }
    };

    const loadMessages = async (chat) => {
        setSelectedChat(chat);
        console.log("📂 Cargando mensajes del chat:", chat);
        try {
            const res = await axios.get(`/api/chats/${chat.id}/mensajes`);
            console.log("📩 Mensajes obtenidos:", res.data);
            setMessages(res.data.mensajes || []);

            if (chat.tipo === 'grupo') {
                await loadGroupParticipants(chat.id);
            }
        } catch (err) {
            console.error("❌ Error al cargar mensajes", err);
            setMessages([]);
        }
    };

    const loadGroupParticipants = async (chatId) => {
        setLoadingParticipants(true);
        try {
            console.log("👥 Cargando participantes del chat:", chatId);
            const res = await axios.get(`/api/chats/${chatId}/participantes`);
            console.log("👥 Participantes recibidos:", res.data);
            setGroupParticipants(res.data.participantes || []);
        } catch (err) {
            console.error("❌ Error al cargar participantes:", err);
            console.error("❌ Detalles:", err.response?.data);
            setGroupParticipants([]);
        } finally {
            setLoadingParticipants(false);
        }
    };

    const sendMessage = () => {
        if (!message.trim() || !selectedChat) return;

        console.log("📤 Enviando mensaje:", { chat_id: selectedChat.id, content: message });
        socket.emit("sendMessage", {
            chat_id: selectedChat.id,
            content: message,
        });

        setMessage("");
    };

    const searchUser = async () => {
        if (!search.trim()) return;
        console.log("🔍 Buscando usuario:", search);
        try {
            const res = await axios.get(
                `/api/usuarios/buscar?q=${encodeURIComponent(search)}`
            );
            console.log("✅ Usuarios encontrados:", res.data);
            setFoundUsers(res.data.usuarios || []);
        } catch (err) {
            console.error("❌ Error buscando usuarios:", err);
            setFoundUsers([]);
        }
    };

    const createDirectChat = async (userId) => {
        console.log("🆕 Creando chat directo con usuario:", userId);
        setLoading(true);

        try {
            const res = await axios.post("/api/chats", {
                userId,
                nombre: null
            });

            console.log("✅ Respuesta del servidor:", res.data);

            const chatCreado = res.data.chat;

            await fetchChats();
            setSelectedChat(chatCreado);
            await loadMessages(chatCreado);

            setSearch("");
            setFoundUsers([]);

            console.log("✅ Chat creado exitosamente:", chatCreado);

        } catch (err) {
            console.error("❌ Error al crear chat:", err);
            showNotification("Error al crear chat: " + (err.response?.data?.error || err.message), "error");
        } finally {
            setLoading(false);
        }
    };

    const createGroup = async () => {
        if (!groupName.trim()) {
            showNotification("El nombre del grupo es requerido", "error");
            return;
        }

        if (selectedUsers.length === 0) {
            showNotification("Debes seleccionar al menos un usuario", "error");
            return;
        }

        setLoading(true);
        try {
            const res = await axios.post("/api/chats", {
                nombre: groupName,
                tipo: 'grupo',
                participantes: selectedUsers
            });

            console.log("✅ Grupo creado:", res.data);

            await fetchChats();
            setShowCreateGroup(false);
            setGroupName("");
            setSelectedUsers([]);
            setSearch("");
            setFoundUsers([]);

            showNotification("Grupo creado exitosamente", "success");
        } catch (err) {
            console.error("❌ Error al crear grupo:", err);
            showNotification("Error al crear grupo: " + (err.response?.data?.error || err.message), "error");
        } finally {
            setLoading(false);
        }
    };

    const toggleUserSelection = (userId) => {
        setSelectedUsers(prev => {
            if (prev.includes(userId)) {
                return prev.filter(id => id !== userId);
            } else {
                return [...prev, userId];
            }
        });
    };

    const addMemberToGroup = async (userId) => {
        if (!selectedChat) return;

        try {
            await axios.post(`/api/chats/${selectedChat.id}/participantes`, {
                usuario_id: userId
            });

            showNotification("Usuario agregado al grupo exitosamente", "success");
            await loadGroupParticipants(selectedChat.id);
            setShowAddMember(false);
            setSearch("");
            setFoundUsers([]);
        } catch (err) {
            console.error("❌ Error al agregar miembro:", err);
            showNotification(err.response?.data?.error || "Error al agregar miembro", "error");
        }
    };

    const changeParticipantRole = async (userId, newRole) => {
        if (!selectedChat) return;

        try {
            await axios.put(`/api/chats/${selectedChat.id}/participantes/${userId}`, {
                rol: newRole
            });

            showNotification("Rol actualizado correctamente", "success");
            await loadGroupParticipants(selectedChat.id);
        } catch (err) {
            console.error("❌ Error al cambiar rol:", err);
            showNotification(err.response?.data?.error || "Error al cambiar rol", "error");
        }
    };

    const removeParticipant = async (userId) => {
        if (!selectedChat) return;

        showConfirm("¿Estás seguro de eliminar este participante del grupo?", async () => {
            try {
                await axios.delete(`/api/chats/${selectedChat.id}/participantes/${userId}`);

                showNotification("Participante eliminado del grupo", "success");
                await loadGroupParticipants(selectedChat.id);
            } catch (err) {
                console.error("❌ Error al eliminar participante:", err);
                showNotification(err.response?.data?.error || "Error al eliminar participante", "error");
            }
        });
    };

    const deleteMessage = async (messageId) => {
        if (!selectedChat) return;

        showConfirm("¿Estás seguro de eliminar este mensaje?", async () => {
            try {
                await axios.delete(`/api/chats/${selectedChat.id}/mensajes/${messageId}`);

                setMessages(prev => prev.filter(m => m.id !== messageId));
                showNotification("Mensaje eliminado", "success");
            } catch (err) {
                console.error("❌ Error al eliminar mensaje:", err);
                showNotification(err.response?.data?.error || "Error al eliminar mensaje", "error");
            }
        });
    };

    const deleteChat = async () => {
        if (!selectedChat) return;

        const chatName = getChatName(selectedChat);
        const message = selectedChat.tipo === 'grupo'
            ? `¿Estás seguro de eliminar el grupo "${chatName}"? Esta acción no se puede deshacer.`
            : `¿Estás seguro de eliminar este chat con ${chatName}? Esta acción no se puede deshacer.`;

        showConfirm(message, async () => {
            try {
                await axios.delete(`/api/chats/${selectedChat.id}`);

                showNotification(
                    selectedChat.tipo === 'grupo' ? "Grupo eliminado" : "Chat eliminado",
                    "success"
                );

                setSelectedChat(null);
                setMessages([]);
                await fetchChats();
            } catch (err) {
                console.error("❌ Error al eliminar chat:", err);
                showNotification(err.response?.data?.error || "Error al eliminar chat", "error");
            }
        });
    };

    const leaveGroup = async () => {
        if (!selectedChat || selectedChat.tipo !== 'grupo') return;

        showConfirm(`¿Estás seguro de salir del grupo "${getChatName(selectedChat)}"?`, async () => {
            try {
                await axios.delete(`/api/chats/${selectedChat.id}/participantes/${currentUserId}`);

                showNotification("Has salido del grupo", "success");

                setSelectedChat(null);
                setMessages([]);
                await fetchChats();
            } catch (err) {
                console.error("❌ Error al salir del grupo:", err);
                showNotification(err.response?.data?.error || "Error al salir del grupo", "error");
            }
        });
    };

    const handleTyping = () => {
        if (selectedChat) {
            socket.emit("typing", {
                chatId: selectedChat.id,
                userId: currentUserId
            });
        }
    };

    const getChatName = (chat) => {
        if (chat.tipo === 'grupo') {
            return chat.nombre || chat.titulo || "Grupo Sin Nombre";
        }

        if (chat.tipo === 'directo' && chat.otro_usuario) {
            return `${chat.otro_usuario.nombre} ${chat.otro_usuario.apellido}`;
        }

        return "Chat";
    };

    const isGroupAdmin = () => {
        if (!selectedChat || selectedChat.tipo !== 'grupo') return false;
        return selectedChat.creado_por === currentUserId;
    };

    const openGroupModal = async () => {
        if (!selectedChat) return;
        setShowGroupModal(true);
        await loadGroupParticipants(selectedChat.id);
    };

    return (
        <div className="dashboard-root">
            <Sidebar isOpen={isOpen} setIsOpen={setIsOpen} />

            <div className="chat-container">
                <Header title="Chats" />
                <div className="chat-content-wrapper">

                    <div className="chat-list">
                        <div className="search-box">
                            <button
                                onClick={() => setShowCreateGroup(true)}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    background: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    marginBottom: '10px',
                                    fontWeight: 'bold',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                }}
                            >
                                <UserPlus size={18} />
                                Crear Grupo
                            </button>

                            <input
                                type="text"
                                placeholder="Buscar usuario..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onKeyDown={(e) => e.key === "Enter" && searchUser()}
                            />
                            <button
                                onClick={searchUser}
                                disabled={loading}
                                style={{
                                    marginTop: '5px',
                                    width: '100%',
                                    padding: '6px',
                                    background: loading ? '#666' : '#007bff',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: loading ? 'not-allowed' : 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}
                            >
                                <Search size={16} />
                                {loading ? 'Cargando...' : 'Buscar Usuario'}
                            </button>

                            {foundUsers.length > 0 && !showCreateGroup && (
                                <div className="user-results">
                                    {foundUsers.map((u) => (
                                        <div
                                            key={u.id}
                                            onClick={() => !loading && createDirectChat(u.id)}
                                            className="user-result"
                                            style={{
                                                cursor: loading ? 'not-allowed' : 'pointer',
                                                opacity: loading ? 0.6 : 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px'
                                            }}
                                        >
                                            <User size={16} />
                                            {u.nombre} {u.apellido}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <div className="chat-list-items">
                            {chats.length === 0 ? (
                                <div style={{ padding: '15px', color: '#999', textAlign: 'center' }}>
                                    No hay chats. Busca un usuario o crea un grupo.
                                </div>
                            ) : (
                                chats.map((c) => (
                                    <div
                                        key={c.id}
                                        className={`chat-item ${selectedChat?.id === c.id ? "active" : ""}`}
                                        onClick={() => loadMessages(c)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            {c.tipo === 'grupo' ? (
                                                <Users size={18} style={{ color: '#007bff' }} />
                                            ) : (
                                                <MessageCircle size={18} style={{ color: '#28a745' }} />
                                            )}
                                            <div>
                                                <span style={{ fontWeight: '500' }}>
                                                    {getChatName(c)}
                                                </span>
                                                {c.tipo === 'grupo' && (
                                                    <span style={{
                                                        fontSize: '11px',
                                                        color: '#888',
                                                        marginLeft: '8px'
                                                    }}>
                                                        Grupo
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    <div className="chat-main">
                        {selectedChat ? (
                            <>
                                <div className="chat-header">
                                    <h2>{getChatName(selectedChat)}</h2>
                                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                        {selectedChat.tipo === 'grupo' && (
                                            <button
                                                onClick={openGroupModal}
                                                style={{
                                                    padding: '6px 12px',
                                                    background: '#007bff',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <Users size={18} />
                                                Ver Miembros
                                            </button>
                                        )}

                                        <div style={{ position: 'relative' }} className="chat-options-menu">
                                            <button
                                                onClick={() => setShowChatOptions(!showChatOptions)}
                                                style={{
                                                    padding: '6px 10px',
                                                    background: '#6c757d',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    cursor: 'pointer',
                                                    display: 'flex',
                                                    alignItems: 'center'
                                                }}
                                            >
                                                <MoreVertical size={18} />
                                            </button>

                                            {showChatOptions && (
                                                <div style={{
                                                    position: 'absolute',
                                                    right: 0,
                                                    top: '40px',
                                                    background: '#20252d',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                                                    minWidth: '200px',
                                                    zIndex: 100,
                                                    overflow: 'hidden'
                                                }}>
                                                    {selectedChat.tipo === 'grupo' && !isGroupAdmin() && (
                                                        <button
                                                            onClick={() => {
                                                                setShowChatOptions(false);
                                                                leaveGroup();
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px 16px',
                                                                background: 'transparent',
                                                                color: '#ffc107',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '10px',
                                                                textAlign: 'left',
                                                                fontSize: '14px'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.background = '#2a3039'}
                                                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                        >
                                                            <LogOut size={16} />
                                                            Salir del Grupo
                                                        </button>
                                                    )}

                                                    {(selectedChat.tipo === 'directo' || isGroupAdmin()) && (
                                                        <button
                                                            onClick={() => {
                                                                setShowChatOptions(false);
                                                                deleteChat();
                                                            }}
                                                            style={{
                                                                width: '100%',
                                                                padding: '12px 16px',
                                                                background: 'transparent',
                                                                color: '#dc3545',
                                                                border: 'none',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: '10px',
                                                                textAlign: 'left',
                                                                fontSize: '14px'
                                                            }}
                                                            onMouseEnter={(e) => e.target.style.background = '#2a3039'}
                                                            onMouseLeave={(e) => e.target.style.background = 'transparent'}
                                                        >
                                                            <Trash2 size={16} />
                                                            {selectedChat.tipo === 'grupo' ? 'Eliminar Grupo' : 'Eliminar Chat'}
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="messages">
                                    {messages.length === 0 ? (
                                        <div style={{
                                            textAlign: 'center',
                                            color: '#999',
                                            marginTop: '20px'
                                        }}>
                                            No hay mensajes aún. ¡Envía el primero!
                                        </div>
                                    ) : (
                                        messages.map((m) => (
                                            <div
                                                key={m.id}
                                                className={`message ${m.remitente_id === currentUserId ? "mine" : "other"
                                                    }`}
                                                style={{
                                                    position: 'relative',
                                                    display: 'flex',
                                                    flexDirection: 'column'
                                                }}
                                                onMouseEnter={() => setMessageMenuOpen(m.id)}
                                                onMouseLeave={() => setMessageMenuOpen(null)}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' }}>
                                                    <div style={{ flex: 1 }}>{m.contenido || m.content}</div>
                                                    {m.remitente_id === currentUserId && messageMenuOpen === m.id && (
                                                        <button
                                                            onClick={() => deleteMessage(m.id)}
                                                            style={{
                                                                background: 'rgba(220, 53, 69, 0.8)',
                                                                border: 'none',
                                                                color: 'white',
                                                                padding: '4px 6px',
                                                                borderRadius: '4px',
                                                                cursor: 'pointer',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                fontSize: '12px'
                                                            }}
                                                        >
                                                            <Trash2 size={12} />
                                                        </button>
                                                    )}
                                                </div>
                                                {m.remitente_nombre && m.remitente_id !== currentUserId && (
                                                    <small style={{
                                                        fontSize: '11px',
                                                        opacity: 0.7,
                                                        display: 'block',
                                                        marginTop: '3px'
                                                    }}>
                                                        {m.remitente_nombre}
                                                    </small>
                                                )}
                                            </div>
                                        ))
                                    )}

                                    {typingUser && (
                                        <div className="typing-indicator">
                                            💬 Usuario escribiendo...
                                        </div>
                                    )}
                                    <div ref={messagesEndRef} />
                                </div>

                                <div className="chat-input">
                                    <input
                                        type="text"
                                        placeholder="Escribe un mensaje..."
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        onKeyDown={(e) => {
                                            handleTyping();
                                            if (e.key === "Enter") sendMessage();
                                        }}
                                    />
                                    <button onClick={sendMessage} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Send size={18} />
                                        Enviar
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="empty-chat">
                                Selecciona un chat o busca un usuario para comenzar
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {showCreateGroup && (
                <div className="modal-overlay" onClick={() => setShowCreateGroup(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Users size={24} />
                            Crear Grupo
                        </h2>
                        <input
                            type="text"
                            placeholder="Nombre del grupo"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '8px',
                                marginBottom: '10px',
                                background: '#2a3039',
                                border: 'none',
                                color: '#fff',
                                borderRadius: '4px'
                            }}
                        />

                        <input
                            type="text"
                            placeholder="Buscar usuarios para agregar..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && searchUser()}
                            style={{
                                width: '100%',
                                padding: '8px',
                                marginBottom: '5px',
                                background: '#2a3039',
                                border: 'none',
                                color: '#fff',
                                borderRadius: '4px'
                            }}
                        />
                        <button
                            onClick={searchUser}
                            style={{
                                width: '100%',
                                padding: '6px',
                                marginBottom: '10px',
                                background: '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px'
                            }}
                        >
                            <Search size={16} />
                            Buscar
                        </button>

                        {foundUsers.length > 0 && (
                            <div style={{
                                maxHeight: '200px',
                                overflowY: 'auto',
                                marginBottom: '10px',
                                background: '#1c2128',
                                borderRadius: '4px',
                                padding: '5px'
                            }}>
                                {foundUsers.map((u) => (
                                    <div
                                        key={u.id}
                                        onClick={() => toggleUserSelection(u.id)}
                                        style={{
                                            padding: '8px',
                                            cursor: 'pointer',
                                            background: selectedUsers.includes(u.id) ? '#007bff' : 'transparent',
                                            borderRadius: '4px',
                                            marginBottom: '2px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '8px'
                                        }}
                                    >
                                        <User size={16} />
                                        {selectedUsers.includes(u.id) ? '✓ ' : ''}
                                        {u.nombre} {u.apellido}
                                    </div>
                                ))}
                            </div>
                        )}

                        {selectedUsers.length > 0 && (
                            <div style={{ marginBottom: '10px', color: '#bbb' }}>
                                {selectedUsers.length} usuario(s) seleccionado(s)
                            </div>
                        )}

                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={createGroup}
                                disabled={loading}
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    background: loading ? '#666' : '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: loading ? 'not-allowed' : 'pointer'
                                }}
                            >
                                {loading ? 'Creando...' : 'Crear Grupo'}
                            </button>
                            <button
                                onClick={() => {
                                    setShowCreateGroup(false);
                                    setGroupName("");
                                    setSelectedUsers([]);
                                    setSearch("");
                                    setFoundUsers([]);
                                }}
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer'
                                }}
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showGroupModal && selectedChat && (
                <div className="modal-overlay" onClick={() => setShowGroupModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <Users size={24} />
                            Miembros de {getChatName(selectedChat)}
                        </h2>

                        {isGroupAdmin() && (
                            <button
                                onClick={() => setShowAddMember(true)}
                                style={{
                                    width: '100%',
                                    padding: '8px',
                                    marginBottom: '15px',
                                    background: '#28a745',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px'
                                }}
                            >
                                <UserPlus size={18} />
                                Agregar Miembro
                            </button>
                        )}

                        {showAddMember && (
                            <div style={{ marginBottom: '15px', padding: '10px', background: '#1c2128', borderRadius: '4px' }}>
                                <input
                                    type="text"
                                    placeholder="Buscar usuario..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && searchUser()}
                                    style={{
                                        width: '100%',
                                        padding: '8px',
                                        marginBottom: '5px',
                                        background: '#2a3039',
                                        border: 'none',
                                        color: '#fff',
                                        borderRadius: '4px'
                                    }}
                                />
                                <button
                                    onClick={searchUser}
                                    style={{
                                        width: '100%',
                                        padding: '6px',
                                        background: '#007bff',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer',
                                        marginBottom: '5px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    <Search size={16} />
                                    Buscar
                                </button>
                                {foundUsers.length > 0 && (
                                    <div style={{ maxHeight: '150px', overflowY: 'auto' }}>
                                        {foundUsers.map((u) => (
                                            <div
                                                key={u.id}
                                                onClick={() => addMemberToGroup(u.id)}
                                                style={{
                                                    padding: '6px',
                                                    cursor: 'pointer',
                                                    background: '#2a3039',
                                                    borderRadius: '4px',
                                                    marginBottom: '2px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: '6px'
                                                }}
                                            >
                                                <User size={16} />
                                                {u.nombre} {u.apellido}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                <button
                                    onClick={() => {
                                        setShowAddMember(false);
                                        setSearch("");
                                        setFoundUsers([]);
                                    }}
                                    style={{
                                        width: '100%',
                                        padding: '6px',
                                        marginTop: '5px',
                                        background: '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        cursor: 'pointer'
                                    }}
                                >
                                    Cancelar
                                </button>
                            </div>
                        )}

                        {loadingParticipants ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                Cargando participantes...
                            </div>
                        ) : groupParticipants.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: '20px', color: '#999' }}>
                                No se encontraron participantes
                            </div>
                        ) : (
                            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                {groupParticipants.map((p) => (
                                    <div
                                        key={p.usuario_id}
                                        style={{
                                            padding: '10px',
                                            marginBottom: '8px',
                                            background: '#1c2128',
                                            borderRadius: '4px',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center'
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                            {p.rol === 'admin' ? (
                                                <Crown size={18} style={{ color: '#ffc107' }} />
                                            ) : (
                                                <User size={18} style={{ color: '#6c757d' }} />
                                            )}
                                            <div>
                                                <div style={{ fontWeight: '500' }}>
                                                    {p.nombre} {p.apellido}
                                                    {p.usuario_id === currentUserId && ' (Tú)'}
                                                </div>
                                                <div style={{ fontSize: '12px', color: '#888' }}>
                                                    {p.rol === 'admin' ? 'Administrador' : 'Miembro'}
                                                </div>
                                            </div>
                                        </div>

                                        {isGroupAdmin() && p.usuario_id !== currentUserId && p.usuario_id !== selectedChat.creado_por && (
                                            <div style={{ display: 'flex', gap: '5px' }}>
                                                <select
                                                    value={p.rol}
                                                    onChange={(e) => changeParticipantRole(p.usuario_id, e.target.value)}
                                                    style={{
                                                        padding: '4px',
                                                        background: '#2a3039',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer'
                                                    }}
                                                >
                                                    <option value="miembro">Miembro</option>
                                                    <option value="admin">Admin</option>
                                                </select>
                                                <button
                                                    onClick={() => removeParticipant(p.usuario_id)}
                                                    style={{
                                                        padding: '4px 8px',
                                                        background: '#dc3545',
                                                        color: 'white',
                                                        border: 'none',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        display: 'flex',
                                                        alignItems: 'center'
                                                    }}
                                                >
                                                    <Trash2 size={16} />
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <button
                            onClick={() => {
                                setShowGroupModal(false);
                                setShowAddMember(false);
                                setSearch("");
                                setFoundUsers([]);
                            }}
                            style={{
                                width: '100%',
                                padding: '10px',
                                marginTop: '15px',
                                background: '#6c757d',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer'
                            }}
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            )}

            <div style={{
                position: 'fixed',
                top: '20px',
                right: '20px',
                zIndex: 9999,
                display: 'flex',
                flexDirection: 'column',
                gap: '10px',
                maxWidth: '400px'
            }}>
                {notifications.map((notif) => (
                    <div
                        key={notif.id}
                        style={{
                            background: notif.type === 'success' ? '#28a745' :
                                notif.type === 'error' ? '#dc3545' : '#007bff',
                            color: 'white',
                            padding: '15px 20px',
                            borderRadius: '8px',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            animation: 'slideInRight 0.3s ease',
                            minWidth: '300px'
                        }}
                    >
                        {notif.type === 'success' && <CheckCircle size={20} />}
                        {notif.type === 'error' && <AlertCircle size={20} />}
                        {notif.type === 'info' && <Info size={20} />}
                        <span style={{ flex: 1 }}>{notif.message}</span>
                        <button
                            onClick={() => setNotifications(prev => prev.filter(n => n.id !== notif.id))}
                            style={{
                                background: 'transparent',
                                border: 'none',
                                color: 'white',
                                cursor: 'pointer',
                                padding: '0',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            <X size={18} />
                        </button>
                    </div>
                ))}
            </div>

            {showConfirmModal && (
                <div className="modal-overlay" onClick={handleCancelConfirm}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '400px' }}>
                        <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                            <AlertCircle size={24} style={{ color: '#ffc107' }} />
                            Confirmación
                        </h2>
                        <p style={{ margin: '20px 0', color: '#ddd', fontSize: '15px' }}>
                            {confirmAction?.message}
                        </p>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={handleConfirm}
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    background: '#dc3545',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                }}
                            >
                                Confirmar
                            </button>
                            <button
                                onClick={handleCancelConfirm}
                                style={{
                                    flex: 1,
                                    padding: '10px',
                                    background: '#6c757d',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontWeight: '500'
                                }}
                            >
                                Cancelar
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}